/** Running bundle id. The deployed id is fetched from /version.json, not from this string alone. */
export function getRunningVersion(): string {
  const version = process.env.NEXT_PUBLIC_BUILD_SHA || process.env.NEXT_PUBLIC_COMMIT_SHA || 'dev';
  return version.trim() || 'dev';
}

export function isNewerDeployment(running: string, deployed: string | null | undefined): boolean {
  const current = running.trim();
  const next = (deployed || '').trim();
  if (!current || !next || current === 'dev' || next === 'dev') return false;
  return current !== next;
}

/** Live deploy id. Failure returns null so the dashboard keeps rendering. */
export async function fetchDeployedVersion(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const payload = (await response.json()) as { version?: unknown };
    const version = typeof payload?.version === 'string' ? payload.version.trim() : '';
    return version || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

const RELOAD_PREFIX = 'eh_asset_reload:';

export function hasReloadedFor(versionKey: string): boolean {
  if (typeof window === 'undefined' || !versionKey) return false;
  try {
    return sessionStorage.getItem(RELOAD_PREFIX + versionKey) === '1';
  } catch {
    return false;
  }
}

/** Navigate to this document with a cache-busting query so the latest HTML is requested. */
export function reloadToVersion(version: string): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('_ehv', version || String(Date.now()));
  window.location.replace(url.toString());
}

/**
 * One navigation per version key. The key is stored before navigation so a
 * second mount of the same build cannot reload again.
 */
export function reloadOnce(
  versionKey: string,
  navigate: (version: string) => void = reloadToVersion,
): boolean {
  if (typeof window === 'undefined' || !versionKey) return false;
  if (hasReloadedFor(versionKey)) return false;
  try {
    sessionStorage.setItem(RELOAD_PREFIX + versionKey, '1');
  } catch {
    return false;
  }
  navigate(versionKey);
  return true;
}

export function wasUpdateDismissed(version: string): boolean {
  if (typeof window === 'undefined' || !version) return false;
  try {
    return sessionStorage.getItem(`eh_update_dismissed:${version}`) === '1';
  } catch {
    return false;
  }
}

export function dismissUpdate(version: string): void {
  if (typeof window === 'undefined' || !version) return;
  try {
    sessionStorage.setItem(`eh_update_dismissed:${version}`, '1');
  } catch {
    /* private mode */
  }
}

/** Drop the cache-buster after load so it is not part of later navigations. */
export function stripVersionQuery(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('_ehv')) return;
  url.searchParams.delete('_ehv');
  window.history.replaceState(window.history.state, '', url.toString());
}
